import React, { useEffect, useState } from "react";
import { useNavigate, useParams } from "react-router-dom";
import axios from "axios";
import { toast } from "sonner";
import {
  X, ChevronLeft, ChevronRight, Phone, Mail, Gauge, Calendar, Fuel,
  Settings2, ShieldCheck, FileText, CheckCircle2, AlertCircle, Car, Share2,
} from "lucide-react";
import { COMPANY } from "@/data/content";

const API = `${process.env.REACT_APP_BACKEND_URL}/api`;
const photoUrl = (p) => `${API}/vehicle-photos/${p}`;

const fmtPrice = (v) =>
  `£${Number(v).toLocaleString("en-GB", { maximumFractionDigits: 0 })}`;
const fmtMiles = (v) => `${Number(v).toLocaleString("en-GB")} miles`;

export default function SalesPage() {
  const [vehicles, setVehicles] = useState([]);
  const [loading, setLoading] = useState(true);
  const { vehicleId } = useParams();
  const navigate = useNavigate();

  useEffect(() => {
    axios
      .get(`${API}/vehicles`)
      .then((res) => setVehicles(res.data.vehicles || []))
      .catch(() => setVehicles([]))
      .finally(() => setLoading(false));
  }, []);

  const selected = vehicleId ? vehicles.find((v) => v.id === vehicleId) : null;

  useEffect(() => {
    if (!loading && vehicleId && !selected) {
      toast.error("That vehicle listing is no longer available.");
      navigate("/sales", { replace: true });
    }
  }, [loading, vehicleId, selected, navigate]);

  return (
    <section className="section" data-testid="sales-page">
      <div className="container-owrs">
        <div className="overline mb-4">Vehicle Sales</div>
        <h1 className="font-display text-4xl sm:text-5xl lg:text-6xl font-medium tracking-tighter leading-[1.05] mb-6">
          Vehicles <span className="text-[var(--owrs-accent)]">for sale.</span>
        </h1>
        <p className="text-base text-[var(--owrs-text-muted)] leading-relaxed max-w-2xl mb-12">
          A small, hand-picked selection of vehicles prepared and checked in our own
          workshop. Interested in something below? Send an enquiry or call us on{" "}
          <a
            href={`tel:${COMPANY.phoneTel}`}
            className="text-[var(--owrs-accent)] underline-offset-4 hover:underline"
          >
            {COMPANY.phone}
          </a>
          .
        </p>

        {loading ? (
          <div className="surface p-10 text-center text-[var(--owrs-text-muted)]" data-testid="sales-loading">
            Loading vehicles…
          </div>
        ) : vehicles.length === 0 ? (
          <div className="surface p-10 sm:p-14 text-center" data-testid="sales-empty">
            <Car className="h-8 w-8 mx-auto mb-4 text-[var(--owrs-accent)]" />
            <div className="font-display text-xl sm:text-2xl mb-2 uppercase">
              Nothing listed right now
            </div>
            <p className="text-sm text-[var(--owrs-text-muted)] max-w-md mx-auto">
              Our stock changes regularly. Check back soon, or call us on{" "}
              {COMPANY.phone} to hear what's coming in.
            </p>
          </div>
        ) : (
          <div className="grid sm:grid-cols-2 lg:grid-cols-3 gap-6 sm:gap-8" data-testid="sales-grid">
            {vehicles.map((v) => (
              <VehicleCard key={v.id} vehicle={v} onClick={() => navigate(`/sales/${v.id}`)} />
            ))}
          </div>
        )}
      </div>

      {selected && (
        <VehicleModal vehicle={selected} onClose={() => navigate("/sales")} />
      )}
    </section>
  );
}

function VehicleCard({ vehicle: v, onClick }) {
  const sold = v.status === "sold";
  return (
    <button
      onClick={onClick}
      data-testid={`vehicle-card-${v.id}`}
      className="text-left surface group hover:border-[var(--owrs-accent)]/50 transition-colors overflow-hidden"
    >
      <div className="relative aspect-[4/3] bg-[var(--owrs-surface-2,rgba(255,255,255,0.04))] overflow-hidden">
        {v.photos?.length > 0 ? (
          <img
            src={photoUrl(v.photos[0])}
            alt={`${v.make} ${v.model}`}
            loading="lazy"
            className={`h-full w-full object-contain transition-transform duration-500 group-hover:scale-[1.03] ${sold ? "opacity-50 grayscale-[40%]" : ""}`}
          />
        ) : (
          <div className="h-full w-full flex items-center justify-center text-[var(--owrs-text-dim)]">
            <Car className="h-10 w-10" />
          </div>
        )}
        {sold && (
          <div
            data-testid={`vehicle-sold-badge-${v.id}`}
            className="absolute top-4 left-4 px-4 py-1.5 bg-[var(--owrs-accent)] text-[var(--owrs-accent-ink)] font-display text-sm tracking-[0.3em] uppercase"
          >
            Sold
          </div>
        )}
      </div>
      <div className="p-5 sm:p-6">
        <div className="text-xs tracking-[0.24em] uppercase text-[var(--owrs-text-dim)] mb-1.5">
          {v.year} · {v.fuel_type}
        </div>
        <div className="font-display text-xl sm:text-2xl uppercase leading-tight mb-3 group-hover:text-[var(--owrs-accent)] transition-colors">
          {v.make} {v.model}
        </div>
        <div className="flex items-center justify-between gap-3">
          <span className="font-display text-lg text-[var(--owrs-accent)]">
            {fmtPrice(v.price)}
          </span>
          <span className="text-xs text-[var(--owrs-text-muted)]">{fmtMiles(v.mileage)}</span>
        </div>
      </div>
    </button>
  );
}

function VehicleModal({ vehicle: v, onClose }) {
  const [idx, setIdx] = useState(0);
  const [enquiring, setEnquiring] = useState(false);
  const photos = v.photos || [];
  const sold = v.status === "sold";

  useEffect(() => {
    document.body.style.overflow = "hidden";
    return () => { document.body.style.overflow = ""; };
  }, []);

  const share = async () => {
    const url = `${window.location.origin}/sales/${v.id}`;
    const title = `${v.year} ${v.make} ${v.model} — ${fmtPrice(v.price)} | OWRS Automotive`;
    if (navigator.share) {
      try {
        await navigator.share({ title, url });
        return;
      } catch {
        // user cancelled — fall through to clipboard
      }
    }
    try {
      await navigator.clipboard.writeText(url);
      toast.success("Link copied — paste it anywhere to share this advert.");
    } catch {
      window.prompt("Copy this link:", url);
    }
  };

  return (
    <div
      className="fixed inset-0 z-[60] flex items-start justify-center p-3 sm:p-6 overflow-y-auto"
      data-testid="vehicle-modal"
    >
      <div className="fixed inset-0 bg-black/75 backdrop-blur-sm" onClick={onClose} />
      <div className="relative w-full max-w-4xl my-6 bg-[var(--owrs-surface)] border border-[var(--owrs-border-strong)] shadow-2xl">
        <button
          onClick={onClose}
          data-testid="vehicle-modal-close"
          aria-label="Close"
          className="absolute top-3 right-3 z-10 h-10 w-10 inline-flex items-center justify-center bg-[rgba(13,31,45,0.85)] border border-[var(--owrs-border-strong)] text-[var(--owrs-text)] hover:text-[var(--owrs-accent)] transition-colors"
        >
          <X className="h-5 w-5" />
        </button>
        <button
          onClick={share}
          data-testid="vehicle-share-btn"
          aria-label="Share this advert"
          title="Share this advert"
          className="absolute top-3 right-16 z-10 h-10 px-3 inline-flex items-center gap-2 bg-[rgba(13,31,45,0.85)] border border-[var(--owrs-border-strong)] text-[var(--owrs-text)] hover:text-[var(--owrs-accent)] transition-colors text-xs tracking-[0.18em] uppercase"
        >
          <Share2 className="h-4 w-4" />
          <span className="hidden sm:inline">Share</span>
        </button>

        {/* Photos */}
        <div className="relative aspect-[16/10] bg-black/40 overflow-hidden">
          {photos.length > 0 ? (
            <img
              src={photoUrl(photos[idx])}
              alt={`${v.make} ${v.model} photo ${idx + 1}`}
              className="h-full w-full object-contain"
            />
          ) : (
            <div className="h-full w-full flex items-center justify-center text-[var(--owrs-text-dim)]">
              <Car className="h-12 w-12" />
            </div>
          )}
          {sold && (
            <div className="absolute top-4 left-4 px-5 py-2 bg-[var(--owrs-accent)] text-[var(--owrs-accent-ink)] font-display text-base tracking-[0.3em] uppercase">
              Sold
            </div>
          )}
          {photos.length > 1 && (
            <>
              <button
                onClick={() => setIdx((idx - 1 + photos.length) % photos.length)}
                data-testid="vehicle-photo-prev"
                aria-label="Previous photo"
                className="absolute left-3 top-1/2 -translate-y-1/2 h-10 w-10 inline-flex items-center justify-center bg-[rgba(13,31,45,0.8)] border border-[var(--owrs-border-strong)] hover:text-[var(--owrs-accent)] transition-colors"
              >
                <ChevronLeft className="h-5 w-5" />
              </button>
              <button
                onClick={() => setIdx((idx + 1) % photos.length)}
                data-testid="vehicle-photo-next"
                aria-label="Next photo"
                className="absolute right-3 top-1/2 -translate-y-1/2 h-10 w-10 inline-flex items-center justify-center bg-[rgba(13,31,45,0.8)] border border-[var(--owrs-border-strong)] hover:text-[var(--owrs-accent)] transition-colors"
              >
                <ChevronRight className="h-5 w-5" />
              </button>
              <div className="absolute bottom-3 right-3 px-2.5 py-1 bg-[rgba(13,31,45,0.8)] text-xs tracking-wider">
                {idx + 1} / {photos.length}
              </div>
            </>
          )}
        </div>
        {photos.length > 1 && (
          <div className="flex gap-2 p-3 overflow-x-auto border-b border-[var(--owrs-border)]">
            {photos.map((p, i) => (
              <button
                key={p}
                onClick={() => setIdx(i)}
                className={`shrink-0 h-14 w-20 overflow-hidden border transition-colors ${
                  i === idx ? "border-[var(--owrs-accent)]" : "border-[var(--owrs-border)] opacity-60 hover:opacity-100"
                }`}
              >
                <img src={photoUrl(p)} alt="" className="h-full w-full object-cover" />
              </button>
            ))}
          </div>
        )}

        {/* Details */}
        <div className="p-6 sm:p-8">
          <div className="flex flex-wrap items-start justify-between gap-4 mb-6">
            <div>
              <div className="text-xs tracking-[0.24em] uppercase text-[var(--owrs-text-dim)] mb-1.5">
                {v.year} · {v.fuel_type}
              </div>
              <h2 className="font-display text-2xl sm:text-3xl uppercase leading-tight">
                {v.make} {v.model}
              </h2>
            </div>
            <div className="font-display text-2xl sm:text-3xl text-[var(--owrs-accent)]" data-testid="vehicle-modal-price">
              {fmtPrice(v.price)}
            </div>
          </div>

          <div className="grid grid-cols-2 sm:grid-cols-3 gap-4 mb-6">
            <Spec icon={<Calendar className="h-4 w-4" />} label="Year" value={v.year} />
            <Spec icon={<Gauge className="h-4 w-4" />} label="Mileage" value={fmtMiles(v.mileage)} />
            <Spec icon={<Fuel className="h-4 w-4" />} label="Fuel" value={v.fuel_type} />
            {v.gearbox && <Spec icon={<Settings2 className="h-4 w-4" />} label="Gearbox" value={v.gearbox} />}
            {v.mot_date && <Spec icon={<ShieldCheck className="h-4 w-4" />} label="MOT until" value={v.mot_date} />}
            {v.service_history && <Spec icon={<FileText className="h-4 w-4" />} label="Service history" value={v.service_history} />}
          </div>

          <p className="text-sm sm:text-base text-[var(--owrs-text-muted)] leading-relaxed whitespace-pre-wrap mb-8">
            {v.description}
          </p>

          {enquiring ? (
            <EnquiryForm vehicle={v} onDone={() => setEnquiring(false)} />
          ) : (
            <div className="flex flex-col sm:flex-row sm:items-center gap-4">
              {!sold && (
                <button
                  onClick={() => setEnquiring(true)}
                  data-testid="vehicle-enquire-btn"
                  className="btn-primary"
                >
                  <Mail className="h-4 w-4" /> Enquire about this vehicle
                </button>
              )}
              <a
                href={`tel:${COMPANY.phoneTel}`}
                data-testid="vehicle-call-link"
                className="inline-flex items-center gap-2 text-sm text-[var(--owrs-text-muted)] hover:text-[var(--owrs-accent)] transition-colors"
              >
                <Phone className="h-4 w-4" />
                {sold ? `Looking for something similar? Call ${COMPANY.phone}` : `Or call us on ${COMPANY.phone}`}
              </a>
            </div>
          )}
        </div>
      </div>
    </div>
  );
}

function Spec({ icon, label, value }) {
  return (
    <div>
      <div className="flex items-center gap-2 text-[10px] tracking-[0.24em] uppercase text-[var(--owrs-text-dim)] mb-1">
        <span className="text-[var(--owrs-accent)]">{icon}</span>
        {label}
      </div>
      <div className="text-sm text-[var(--owrs-text)]">{value}</div>
    </div>
  );
}

function EnquiryForm({ vehicle: v, onDone }) {
  const [form, setForm] = useState({
    name: "",
    email: "",
    phone: "",
    message: `Hi, I'm interested in the ${v.year} ${v.make} ${v.model} you have advertised for ${fmtPrice(v.price)}. Please could you get in touch with more details.`,
  });
  const [submitting, setSubmitting] = useState(false);
  const [sent, setSent] = useState(false);
  const [error, setError] = useState("");

  const onChange = (e) => setForm((f) => ({ ...f, [e.target.name]: e.target.value }));

  const onSubmit = async (e) => {
    e.preventDefault();
    setError("");
    setSubmitting(true);
    try {
      await axios.post(`${API}/contact`, form);
      setSent(true);
      toast.success("Enquiry sent — we'll be in touch shortly.");
    } catch {
      setError(`Something went wrong. Please call us on ${COMPANY.phone}.`);
    } finally {
      setSubmitting(false);
    }
  };

  if (sent) {
    return (
      <div
        data-testid="vehicle-enquiry-success"
        className="border border-[var(--owrs-accent)]/40 bg-[var(--owrs-accent)]/5 p-5 flex gap-4"
      >
        <CheckCircle2 className="h-6 w-6 text-[var(--owrs-accent)] shrink-0" />
        <div>
          <div className="font-display text-lg mb-1 uppercase">Enquiry sent</div>
          <p className="text-sm text-[var(--owrs-text-muted)]">
            Thanks — we've received your enquiry about the {v.make} {v.model} and will
            get back to you as soon as we can.
          </p>
        </div>
      </div>
    );
  }

  return (
    <form onSubmit={onSubmit} data-testid="vehicle-enquiry-form" className="border border-[var(--owrs-border)] p-5 sm:p-6 grid sm:grid-cols-2 gap-4">
      <div className="sm:col-span-2 flex items-center justify-between">
        <div className="overline">Enquire — {v.make} {v.model}</div>
        <button type="button" onClick={onDone} className="text-xs text-[var(--owrs-text-dim)] hover:text-[var(--owrs-accent)]">
          Cancel
        </button>
      </div>
      <div>
        <label className="owrs-label" htmlFor="enq-name">Name</label>
        <input id="enq-name" name="name" value={form.name} onChange={onChange}
          data-testid="enquiry-name-input" required maxLength={120}
          className="owrs-input" placeholder="Your full name" />
      </div>
      <div>
        <label className="owrs-label" htmlFor="enq-phone">Phone (optional)</label>
        <input id="enq-phone" name="phone" type="tel" value={form.phone} onChange={onChange}
          data-testid="enquiry-phone-input" className="owrs-input" placeholder="07…" />
      </div>
      <div className="sm:col-span-2">
        <label className="owrs-label" htmlFor="enq-email">Email</label>
        <input id="enq-email" name="email" type="email" value={form.email} onChange={onChange}
          data-testid="enquiry-email-input" required className="owrs-input" placeholder="you@example.com" />
      </div>
      <div className="sm:col-span-2">
        <label className="owrs-label" htmlFor="enq-message">Message</label>
        <textarea id="enq-message" name="message" rows={4} value={form.message} onChange={onChange}
          data-testid="enquiry-message-input" required maxLength={4000} className="owrs-input" />
      </div>
      {error && (
        <div className="sm:col-span-2 border border-red-500/40 bg-red-500/5 p-3 flex gap-3 items-start" data-testid="enquiry-error">
          <AlertCircle className="h-5 w-5 text-red-400 shrink-0 mt-0.5" />
          <div className="text-sm">{error}</div>
        </div>
      )}
      <div className="sm:col-span-2">
        <button type="submit" disabled={submitting} data-testid="enquiry-submit-btn"
          className="btn-primary disabled:opacity-60">
          {submitting ? "Sending…" : "Send Enquiry"}
        </button>
      </div>
    </form>
  );
}
